import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Wordmark from "../Wordmark";

const NAV = [
  { to: "/", label: "Start", end: true },
  { to: "/produkter", label: "Produkter" },
  { to: "/sa-fungerar-det", label: "Så fungerar det" },
  { to: "/om-vido", label: "Om Vido" },
  { to: "/kontakt", label: "Kontakt" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 backdrop-blur-md transition-all ${
        scrolled
          ? "bg-[hsl(var(--surface))]/95 border-b border-[color:hsl(var(--line))] shadow-[0_10px_28px_-28px_rgba(31,29,27,0.45)]"
          : "bg-[hsl(var(--surface))]/80 border-b border-transparent"
      }`}
    >
      <div className="container-vido flex items-center justify-between h-16 md:h-20">
        <Wordmark size="md" />

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.to === "/" ? "start" : n.to.slice(1)}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[color:hsl(var(--ink))]"
                    : "text-[color:hsl(var(--muted-ink))] hover:text-[color:hsl(var(--ink))]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/kontakt"
            data-testid="header-cta-kontakt"
            className="btn-primary"
          >
            Skicka förfrågan
          </Link>
        </div>

        <button
          type="button"
          aria-label={open ? "Stäng meny" : "Öppna meny"}
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-[color:hsl(var(--line))] bg-white/60 text-[color:hsl(var(--ink))]"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden absolute inset-x-0 top-full h-[calc(100dvh-4rem)] md:h-[calc(100dvh-5rem)] z-50 overflow-hidden transition-opacity duration-200 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none invisible"
        }`}
        aria-hidden={!open}
      >
        <div
          data-testid="mobile-menu"
          className={`absolute inset-0 bg-[hsl(var(--surface))] transition-transform duration-300 ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="container-vido flex flex-col h-full py-6 gap-1 overflow-y-auto">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`mobile-nav-${n.to === "/" ? "start" : n.to.slice(1)}`}
                className={({ isActive }) =>
                  `text-2xl py-3 border-b border-[color:hsl(var(--line))] font-serif ${
                    isActive
                      ? "text-[color:hsl(var(--ink))]"
                      : "text-[color:hsl(var(--muted-ink))]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/kontakt"
              data-testid="mobile-cta-kontakt"
              className="btn-primary mt-8 w-full"
            >
              Skicka förfrågan
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
